import React, { useState, useEffect, ChangeEvent, useCallback, useRef } from "react";
import axios from "../utils/axios";
import logger from "../utils/logger";
import CrosswordGrid from "./CrosswordGrid";
import CrosswordClues from "./CrosswordClues";
import CrosswordMode, { Mode } from "./CrosswordMode";
import "./CrosswordGridClues.css";
import { ClueSpec } from "../ClueSpec";
import { Crossword } from "../models/crossword";
import { getUser, loadCurrentUser, refreshAccessToken } from "../utils/auth";
import useJoinGroup from "../hooks/useJoinGroup";
import useReceiveGridUpdate from "../hooks/useReceiveGridUpdate";
import { useVisibilityChange } from "../hooks/useVisibilityChange";
import { fadeTime } from "./CrosswordCell";
import { AmendedGridStateFromUpdate, clueString } from "../utils/GridUtils";
import { sanitizeClueHtml } from "../utils/sanitizeClue";
import { GridCellState } from "../GridCellState";
import { useSignalR } from "../contexts/useSignalR";
import { createGrid } from "../CrosswordUtils";
import { CrosswordSessionDto } from "../models/crossword-session-dto";
import { CrosswordSessionUpdateDto } from "../models/crossword-session-update-dto";
import SessionParticipants from "./SessionParticipants";
import { HubConnectionState } from "@microsoft/signalr";
import { ArchiveBoxXMarkIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

interface CrosswordGridCluesDisplayProps {
  crosswordId: number;
  readonly: boolean;
  sessionId: number;
  mobile?: boolean;
  onShowStats?: () => void;
  loadingStats?: boolean;
}

interface SetterHistory {
  crosswordCount: number;
  firstAppearance?: Date;
  mostRecentPreviousAppearance?: Date;
}

interface SetterHistoryState {
  crosswordId: number;
  history: SetterHistory;
}

const CrosswordGridClues: React.FC<CrosswordGridCluesDisplayProps> = ({
  crosswordId,
  readonly,
  sessionId,
  mobile = false,
  onShowStats,
  loadingStats = false,
}) => {
  const navigate = useNavigate();
  const [selectedMode, setMode] = useState<Mode>("user");
  const [selectedClue, setSelectedClue] = useState<ClueSpec>(new ClueSpec());
  const [crossword, setCrossword] = useState<Crossword | null>(null);
  const [session, setSession] = useState<CrosswordSessionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [gridState, setGridState] = useState<GridCellState[][]>(createGrid(crossword));
  const [scratchpad, setScratchpad] = useState<string>("");
  const [userKey, setUserKey] = useState("@");
  const [updatedCells, setUpdatedCells] = useState<{ row: number; col: number }[]>([]);
  const [updatingUsers, setUpdatingUsers] = useState<Record<string, number>>({}); // userKey to expiry timestamp
  const [isGridDataReady, setIsGridDataReady] = useState(false);
  const [numUsers, setNumUsers] = useState(0);
  const [isPuzzleComplete, setIsPuzzleComplete] = useState(false);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [setterHistory, setSetterHistory] = useState<SetterHistoryState | null>(null);

  const user = getUser() || {
    username: "unknown",
    email: "unknown",
    isAdmin: false,
  };
  const { connection } = useSignalR();

  const scratchpadChanged = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setScratchpad(e.target.value);
      const update: CrosswordSessionUpdateDto = {
        sessionId: sessionId,
        gridState: "",
        rowChanged: -1,
        colChanged: -1,
        scratchpad: e.target.value,
        userCount:0, // only used server->client
      };
      axios.patch<CrosswordSessionUpdateDto>(`/api/cross/session`, update).catch((error) => {
        logger.error("Error patching session:", error);
      });
    },
    [sessionId, setScratchpad]
  );

  const handleClueChange = useCallback(
    (clueSpec: ClueSpec) => {
      setSelectedClue(clueSpec);
    },
    [setSelectedClue]
  );

  const abandonSession = useCallback(async () => {
    if (!session || userKey !== "A" || session.isCompleted || isAbandoning) return;

    if (!window.confirm("Abandon this crossword? It will be removed from completion statistics.")) {
      return;
    }

    setIsAbandoning(true);
    try {
      await axios.post(`/api/cross/session/${session.id}/abandon`);
      navigate("/join-create-session");
    } catch (error) {
      logger.error("Error abandoning session:", error);
      setIsAbandoning(false);
    }
  }, [isAbandoning, navigate, session, userKey]);

  const loadSessionData = useCallback(async () => {
    try {
      setLoading(true);
      let currentCrosswordId = crosswordId;
      let fetchedSession: CrosswordSessionDto | null = null;

      // If we have a session ID, fetch the session first to get the correct crossword ID
      if (sessionId && sessionId > 0) {
        const sessionResponse = await axios.get<CrosswordSessionDto>(
          `/api/cross/session?id=${sessionId}`
        );
        fetchedSession = sessionResponse.data;
        setSession(fetchedSession);
        currentCrosswordId = fetchedSession.crosswordId;

        if (fetchedSession.users) {
          setNumUsers(Object.keys(fetchedSession.users).length);
          if (user.email) {
            const userKeyFromSession = fetchedSession.users[user.email];
            if (userKeyFromSession) {
              setUserKey(userKeyFromSession);
            }
          }
        }
      }

      if (!currentCrosswordId || currentCrosswordId <= 0) {
        setLoading(false);
        return;
      }

      // Fetch crossword structure
      const crosswordResponse = await axios.get<Crossword>(
        `/api/cross/crossword?id=${currentCrosswordId}`
      );
      setCrossword(crosswordResponse.data);

      // Initialize grid
      const baseGrid = createGrid(crosswordResponse.data);
      const populatedGrid = fetchedSession?.gridState
        ? AmendedGridStateFromUpdate(baseGrid, fetchedSession.gridState)
        : baseGrid;
      
      setGridState(populatedGrid);
      setIsGridDataReady(true);

    } catch (error) {
      logger.error("Error loading session data:", error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, crosswordId, user.email]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadSessionData();
  }, [loadSessionData]);

  useEffect(() => {
    if (!crossword?.setter) {
      return;
    }

    let cancelled = false;
    axios.get<SetterHistory>(`/api/cross/crossword/${crossword.id}/setter-history`)
      .then((response) => {
        if (!cancelled) {
          setSetterHistory({ crosswordId: crossword.id, history: response.data });
        }
      })
      .catch((error) => {
        logger.error("Error loading setter history:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [crossword?.id, crossword?.setter]);

  const currentSetterHistory = crossword?.setter && setterHistory?.crosswordId === crossword.id
    ? setterHistory.history
    : null;
  const setterHistoryHint = currentSetterHistory?.firstAppearance
    ? `${currentSetterHistory.crosswordCount} crossword${currentSetterHistory.crosswordCount === 1 ? "" : "s"} set. First appearance: ${new Date(currentSetterHistory.firstAppearance).toLocaleDateString()}.${currentSetterHistory.mostRecentPreviousAppearance ? ` Most recent before this: ${new Date(currentSetterHistory.mostRecentPreviousAppearance).toLocaleDateString()}.` : " This is the first appearance."}`
    : undefined;

  useJoinGroup({ connection, sessionId });
  useReceiveGridUpdate({
    connection,
    sessionId,
    userKey,
    setGridState,
    setScratchpad,
    setUpdatedCells,
    setUpdatingUsers,
    setNumUsers,
  });

  // Keep a ref to the current grid state for diffing on visibility change
  const gridStateRef = useRef<GridCellState[][]>(gridState);
  useEffect(() => {
    gridStateRef.current = gridState;
  }, [gridState]);

  // Catchup handler for when browser tab becomes visible (mobile backgrounding)
  const handleVisibilityCatchup = useCallback(async () => {
    if (!sessionId || sessionId <= 0) return;
    
    // Small delay to allow mobile network to stabilize after resuming from background
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      logger.info('Tab became visible - checking auth and SignalR connection');
      
      // Verify the HttpOnly access token before reconnecting SignalR.
      if (!await loadCurrentUser()) {
        logger.info('Auth token expired - attempting refresh');
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          logger.warn('Token refresh failed - user will need to re-login');
          // Don't attempt catchup with invalid auth
          return;
        }
        logger.info('Auth token refreshed successfully');
      }
      
      // Check SignalR connection state and reconnect if necessary
      if (connection) {
        const state = connection.state;
        if (state === HubConnectionState.Disconnected) {
          logger.info('SignalR disconnected - attempting to reconnect');
          try {
            await connection.start();
            logger.info('SignalR reconnected successfully');
            await connection.invoke('JoinGroup', sessionId);
            logger.info('Rejoined SignalR group after manual reconnect');
          } catch (reconnectError) {
            logger.error('SignalR reconnection failed:', reconnectError);
          }
        } else if (state === HubConnectionState.Disconnecting) {
          logger.info('SignalR is disconnecting - will fetch state but skip reconnect');
        } else if (state === HubConnectionState.Connected) {
          // Connection may have auto-reconnected while backgrounded without rejoining the group
          try {
            await connection.invoke('JoinGroup', sessionId);
            logger.debug('Rejoined SignalR group on visibility restore');
          } catch (rejoinError) {
            logger.error('Failed to rejoin group on visibility restore:', rejoinError);
          }
        } else {
          logger.debug(`SignalR connection state: ${state}`);
        }
      }
      
      const sessionResponse = await axios.get<CrosswordSessionDto>(
        `/api/cross/session?id=${sessionId}`
      );
      const fetchedSession = sessionResponse.data;
      
      if (!fetchedSession.gridState) return;
      
      // Get the current grid and compute what the new grid would be
      const currentGrid = gridStateRef.current;
      const newGrid = AmendedGridStateFromUpdate(currentGrid, fetchedSession.gridState);
      
      // Find cells that changed
      const changedCells: { row: number; col: number }[] = [];
      for (let row = 0; row < newGrid.length; row++) {
        for (let col = 0; col < newGrid[row].length; col++) {
          const oldCell = currentGrid[row]?.[col];
          const newCell = newGrid[row][col];
          if (oldCell && newCell.userText !== oldCell.userText) {
            changedCells.push({ row, col });
          }
        }
      }
      
      // Update the grid state
      setGridState(newGrid);
      setScratchpad(fetchedSession.scratchpad || '');
      
      // Highlight changed cells using the same mechanism as SignalR updates
      if (changedCells.length > 0) {
        logger.info(`Catchup found ${changedCells.length} changed cells`);
        setUpdatedCells((prev) => [...prev, ...changedCells]);
        
        // Clear the highlights after the fade time
        setTimeout(() => {
          setUpdatedCells((prev) =>
            prev.filter(
              (cell) => !changedCells.some((c) => c.row === cell.row && c.col === cell.col)
            )
          );
        }, fadeTime + 100);
      }
    } catch (error) {
      logger.error('Catchup fetch failed:', error);
    }
  }, [sessionId, connection, setGridState, setScratchpad, setUpdatedCells]);

  useVisibilityChange(handleVisibilityCatchup);

  // Optional: Keep event handlers in a separate effect if needed
  useEffect(() => {
    if (connection) {
      connection.on("UserJoined", (joining: { joinee: string; members: string[] }) => {
        logger.debug(`User ${joining.joinee} joined the group. Members: ${joining.members}`);
      });

      connection.on("UserLeft", (leaving: { leaver: string; members: string[] }) => {
        logger.debug(`User ${leaving.leaver} left the group. Members: ${leaving.members}`);
      });

      return () => {
        connection.off("UserJoined");
        connection.off("UserLeft");
      };
    }
  }, [connection]);

  useEffect(() => {
    if (connection) {
      let active = true;
      const handleReconnected = () => {
        if (!active) return;
        logger.info("SignalR reconnected. Reloading session data.");
        loadSessionData();
      };

      connection.onreconnected(handleReconnected);

      return () => {
        active = false;
      };
    }
  }, [connection, loadSessionData]);

  if (loading || !isGridDataReady) {
    return <div className="loading-spinner">Loading crossword...</div>;
  }

  if (!crossword) {
    return <div>Error loading crossword</div>;
  }

  return (
    <div className={"crossword-display" + (readonly ? " watermark" : "")}>
      {/* Desktop layout: grid and full clues side-by-side */}
      {!mobile && (
        <div className="content-area">
          <div className="left-column">
            <div className="grid-header">
              <div className="publication-info">
                <span className="setter-name" title={setterHistoryHint}>{crossword.setter || "Unknown"}</span>
                <div className="participants-with-stats">
                  <SessionParticipants
                    connection={connection}
                    connectionState={connection?.state || HubConnectionState.Disconnected}
                    sessionId={sessionId}
                    updatingUsers={Object.keys(updatingUsers)}
                    isPuzzleComplete={isPuzzleComplete}
                  />
                  {onShowStats && (
                    <button 
                      className="stats-button-grid" 
                      onClick={onShowStats}
                      disabled={loadingStats}
                      title="View Session Statistics"
                    >
                      <ChartBarIcon className="h-5 w-5" />
                    </button>
                  )}
                  {!readonly && userKey === "A" && !session?.isCompleted && (
                    <button
                      className="abandon-button-grid"
                      onClick={abandonSession}
                      disabled={isAbandoning}
                      title="Abandon crossword"
                      aria-label="Abandon crossword"
                    >
                      <ArchiveBoxXMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <span className="publication-date">
                  {crossword.publicationDate
                    ? new Date(crossword.publicationDate).toLocaleDateString()
                    : ""}
                </span>
              </div>
            </div>

            <CrosswordGrid
              grid={gridState}
              readonly={readonly}
              mode={selectedMode}
              selectedClue={selectedClue}
              onClueChange={handleClueChange}
              sessionId={session?.id || 0}
              userKey={userKey}
              loading={loading}
              setGridState={setGridState}
              clues={crossword.clues}
              scratchpad={scratchpad}
              updatedCells={updatedCells}
              numberOfUsers={numUsers}
              onPuzzleCompleteChange={setIsPuzzleComplete}
              startTime={session?.startTime ? new Date(session.startTime) : undefined}
              completionTime={session?.completionTime ? new Date(session.completionTime) : undefined}
              publicationDate={session?.publicationDate ? new Date(session.publicationDate) : undefined}
            />

            <CrosswordMode onModeSelect={setMode} />
            <div className="scratchpad">
              <label htmlFor="scratchpad">Scratchpad:</label>
              <textarea
                id="scratchpad"
                value={scratchpad}
                onChange={scratchpadChanged}
                onFocus={() => handleClueChange(new ClueSpec())}
              />
            </div>
          </div>
          <div className="crossword-clues-container">
            <CrosswordClues
              clues={crossword.clues}
              selectedClue={selectedClue}
              grid={gridState}
              onClueChange={handleClueChange}
              loading={loading}
            />
          </div>
        </div>
      )}
      {/* Mobile layout: grid on top; below a condensed clue view */}
      {mobile && (
        <div className="mobile-content">
          {/* Add publication info header */}
          <div className="grid-header">
            <div className="publication-info">
              <span className="publication-date">
                {crossword.publicationDate
                  ? new Date(crossword.publicationDate).toLocaleDateString()
                  : ""}
              </span>
              <div className="participants-with-stats">
                <SessionParticipants
                  connection={connection}
                  connectionState={connection?.state || HubConnectionState.Disconnected}
                  sessionId={sessionId}
                  updatingUsers={Object.keys(updatingUsers)}
                  isPuzzleComplete={isPuzzleComplete}
                />
                {onShowStats && (
                  <button 
                    className="stats-button-grid" 
                    onClick={onShowStats}
                    disabled={loadingStats}
                    title="View Session Statistics"
                  >
                    <ChartBarIcon className="h-5 w-5" />
                  </button>
                )}
                {!readonly && userKey === "A" && !session?.isCompleted && (
                  <button
                    className="abandon-button-grid"
                    onClick={abandonSession}
                    disabled={isAbandoning}
                    title="Abandon crossword"
                    aria-label="Abandon crossword"
                  >
                    <ArchiveBoxXMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              <span className="setter-name" title={setterHistoryHint}>{crossword.setter || "Unknown"}</span>
            </div>
          </div>

          <CrosswordGrid
            grid={gridState}
            readonly={readonly}
            mode={selectedMode}
            selectedClue={selectedClue}
            onClueChange={handleClueChange}
            sessionId={session?.id || 0}
            userKey={userKey}
            loading={loading}
            setGridState={setGridState}
            clues={crossword.clues}
            scratchpad={scratchpad}
            updatedCells={updatedCells}
            numberOfUsers={numUsers}
            onPuzzleCompleteChange={setIsPuzzleComplete}
            startTime={session?.startTime ? new Date(session.startTime) : undefined}
            completionTime={session?.completionTime ? new Date(session.completionTime) : undefined}
            publicationDate={session?.publicationDate ? new Date(session.publicationDate) : undefined}
          />
          {/* A simplified view for the selected clue */}
          <div className="mobile-clue">
            {selectedClue.isSet() && selectedClue.direction !== "both" ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeClueHtml(clueString(
                    crossword.clues.find(
                      (c) =>
                        c.clueNumber === selectedClue.number &&
                        c.isAcross === (selectedClue.direction === "across")
                    )
                  )),
                }}
              ></span>
            ) : (
              <span>Select a clue</span>
            )}
          </div>
          <CrosswordMode onModeSelect={setMode} />
          <div className="scratchpad">
            <label htmlFor="scratchpad">Scratchpad:</label>
            <textarea
              id="scratchpad"
              value={scratchpad}
              onChange={scratchpadChanged}
              onFocus={() => handleClueChange(new ClueSpec())}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CrosswordGridClues;
